import {
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import type { Tono } from './formato';

/**
 * Las piezas que se repiten en las once pantallas.
 *
 * Cada una lee sus valores de `tokens.css`. Ninguna trae un color, un radio ni
 * un espaciado propio: si hace falta uno que no existe, se agrega primero a
 * `docs/01-design-system.md`.
 */

/**
 * Los atributos de una fila de tabla que se puede abrir.
 *
 * Existe porque `<tr onClick>` a secas no se alcanza con el teclado: quien no
 * usa ratón no podía abrir una orden de compra ni elegir una receta. Va en un
 * solo sitio para que las dos tablas se comporten igual —y las que vengan—.
 *
 * La barra espaciadora hace `preventDefault` porque, si no, además de activar
 * la fila, la página baja una pantalla.
 */
export function filaPulsable(onActivar: () => void) {
  return {
    tabIndex: 0,
    role: 'button' as const,
    className: 'fila--pulsable',
    onClick: onActivar,
    onKeyDown: (evento: KeyboardEvent<HTMLTableRowElement>) => {
      if (evento.key !== 'Enter' && evento.key !== ' ') return;

      evento.preventDefault();
      onActivar();
    },
  };
}

/**
 * Un «?» que explica lo que tiene al lado.
 *
 * El almacén lo usan el dueño, el encargado, quien recibe en la puerta y el
 * contador, y no todos saben qué es un lote FEFO, un costo promedio o un
 * punto de pedido. Se abre al pasar el ratón, al llegar con el tabulador y
 * **al tocarlo**: en la tablet del depósito no hay ratón que pase por encima.
 *
 * La burbuja va fuera de la tabla o del formulario —en el cuerpo de la
 * página— y con posición fija: dentro de una tabla con desplazamiento se
 * cortaba contra su borde.
 */
export function Ayuda({ texto, sobre = '' }: { texto: string; sobre?: string }) {
  const id = useId();
  const boton = useRef<HTMLButtonElement>(null);
  const [lugar, setLugar] = useState<{ top: number; left: number; arriba: boolean } | null>(null);

  const abrir = () => {
    const caja = boton.current?.getBoundingClientRect();
    if (caja === undefined) return;
    // Arriba si cabe; si no —la primera fila de una tabla—, debajo.
    const arriba = caja.top > 140;
    const medio = caja.left + caja.width / 2;
    setLugar({
      top: arriba ? caja.top - 8 : caja.bottom + 8,
      left: Math.min(Math.max(medio, 150), window.innerWidth - 150),
      arriba,
    });
  };
  const cerrar = () => setLugar(null);

  // Al rodar o cambiar el tamaño la burbuja quedaría flotando lejos de su «?».
  useEffect(() => {
    if (lugar === null) return;
    window.addEventListener('scroll', cerrar, true);
    window.addEventListener('resize', cerrar);
    return () => {
      window.removeEventListener('scroll', cerrar, true);
      window.removeEventListener('resize', cerrar);
    };
  }, [lugar]);

  return (
    <>
      <button
        ref={boton}
        type="button"
        className="ayuda"
        aria-label={sobre === '' ? 'Qué significa' : `Qué significa ${sobre}`}
        aria-describedby={lugar === null ? undefined : id}
        aria-expanded={lugar !== null}
        onPointerEnter={(evento) => evento.pointerType === 'mouse' && abrir()}
        onPointerLeave={(evento) => evento.pointerType === 'mouse' && cerrar()}
        onFocus={abrir}
        onBlur={cerrar}
        onClick={(evento) => {
          // Que no active la fila ni el rótulo de al lado. Solo abre: el
          // toque ya le dio el foco —que abre—, y alternar aquí la cerraba
          // en el mismo gesto. Se cierra tocando fuera o con Escape.
          evento.preventDefault();
          evento.stopPropagation();
          abrir();
        }}
        onKeyDown={(evento) => {
          if (evento.key !== 'Escape' || lugar === null) return;
          // Cierra la ayuda, no el formulario de detrás.
          evento.stopPropagation();
          cerrar();
        }}
      >
        ?
      </button>
      {lugar === null
        ? null
        : createPortal(
            <span
              id={id}
              role="tooltip"
              className={`ayuda__burbuja ${lugar.arriba ? 'ayuda__burbuja--arriba' : ''}`}
              style={{ top: lugar.top, left: lugar.left }}
            >
              {texto}
            </span>,
            document.body,
          )}
    </>
  );
}

/**
 * El rótulo de un campo con su ayuda al lado.
 *
 * El «?» va fuera del `<label>`: dentro, tocarlo llevaba el foco al campo y
 * abría el teclado de la tablet en vez de la explicación.
 */
export function Rotulo({ htmlFor, ayuda, children }: { htmlFor: string; ayuda: string; children: ReactNode }) {
  return (
    <span className="rotulo">
      <label htmlFor={htmlFor}>{children}</label>
      <Ayuda texto={ayuda} sobre={typeof children === 'string' ? children.toLowerCase() : ''} />
    </span>
  );
}

/** Una cabecera de columna con su ayuda. */
export function ConAyuda({ children, ayuda }: { children: ReactNode; ayuda: string }) {
  return (
    <span className="con-ayuda">
      {children}
      <Ayuda texto={ayuda} sobre={typeof children === 'string' ? children.toLowerCase() : ''} />
    </span>
  );
}

export function Pastilla({
  texto,
  tono = 'neutro',
}: {
  texto: string;
  tono?: Tono;
}) {
  return <span className={`pastilla pastilla--${tono}`}>{texto}</span>;
}

/** El punto de nivel, que se lee a un palmo en una tabla de veinte filas. */
export function PuntoDeNivel({ tono }: { tono: Tono }) {
  return <i className={`punto punto--${tono}`} aria-hidden="true" />;
}

export function Tarjeta({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}) {
  return <section className={`tarjeta ${className}`}>{children}</section>;
}

/**
 * Una cifra de portada: rótulo en versales, número grande y una línea de apoyo.
 *
 * El apoyo no es decoración: un «8» solo no dice nada, y «1 en nivel crítico»
 * es lo que decide si alguien sale a comprar ahora.
 */
export function Cifra({
  rotulo,
  valor,
  apoyo,
  tono = 'neutro',
  ayuda,
}: {
  rotulo: string;
  valor: string;
  apoyo: string;
  tono?: Tono;
  /** Cómo sale la cifra, para quien no la sabe leer. */
  ayuda?: string;
}) {
  return (
    <div className={`cifra cifra--${tono}`}>
      <p className="versal con-ayuda">
        {rotulo}
        {ayuda === undefined ? null : <Ayuda texto={ayuda} sobre={rotulo.toLowerCase()} />}
      </p>
      <p className="cifra__valor">{valor}</p>
      <p className="cifra__apoyo">{apoyo}</p>
    </div>
  );
}

export function Boton({
  children,
  onClick,
  variante = 'secundario',
  disabled = false,
  type = 'button',
}: {
  children: ReactNode;
  onClick?: () => void;
  variante?: 'primario' | 'secundario' | 'peligro';
  disabled?: boolean;
  type?: 'button' | 'submit';
}) {
  return (
    <button
      type={type}
      className={`boton boton--${variante}`}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
}

/** Píldora de filtro, con su contador al lado cuando lo hay. */
export function Filtro({
  texto,
  cuenta,
  activo,
  onClick,
}: {
  texto: string;
  cuenta?: number;
  activo: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`filtro ${activo ? 'filtro--activo' : ''}`}
      onClick={onClick}
      aria-pressed={activo}
    >
      {texto}
      {cuenta === undefined ? null : <span className="mono">{cuenta}</span>}
    </button>
  );
}

/**
 * Lo que se enseña mientras no hay nada que enseñar.
 *
 * Con su explicación. Una tabla vacía sin una línea que diga por qué se lee como
 * un fallo del programa, y lo normal es que sea un almacén al que todavía no ha
 * entrado mercadería.
 */
export function Vacio({
  titulo,
  mensaje,
  accion,
}: {
  titulo: string;
  mensaje: string;
  accion?: ReactNode;
}) {
  return (
    <div className="vacio">
      <p className="vacio__titulo">{titulo}</p>
      <p className="vacio__mensaje">{mensaje}</p>
      {accion}
    </div>
  );
}

/**
 * Cuando algo falla.
 *
 * Distingue no poder preguntar de que el servidor haya dicho que no, porque lo
 * que toca hacer es distinto: reintentar en un caso, llamar a alguien en el
 * otro.
 */
export function Fallo({ error, onReintentar }: { error: unknown; onReintentar?: () => void }) {
  const esDeRed =
    typeof error === 'object' &&
    error !== null &&
    'isRetryable' in error &&
    (error as { isRetryable: boolean }).isRetryable;

  const mensaje =
    error instanceof Error ? error.message : 'No se pudo completar la operación.';

  return (
    <div className={`fallo ${esDeRed ? 'fallo--red' : ''}`}>
      <p>{mensaje}</p>
      {esDeRed && onReintentar ? (
        <Boton onClick={onReintentar}>Reintentar</Boton>
      ) : null}
    </div>
  );
}

export function Cargando({ que }: { que: string }) {
  return <p className="cargando">Cargando {que}…</p>;
}

/** La cabecera que comparten las once pantallas. */
export function Cabecera({
  titulo,
  subtitulo,
  acciones,
  ayuda,
}: {
  titulo: string;
  subtitulo: string;
  acciones?: ReactNode;
  /** Para qué sirve la pantalla, en dos frases, para quien llega nuevo. */
  ayuda?: string;
}) {
  return (
    <header className="cabecera">
      <div>
        <h1 className="con-ayuda">
          {titulo}
          {ayuda === undefined ? null : <Ayuda texto={ayuda} sobre="esta pantalla" />}
        </h1>
        <p className="cabecera__subtitulo">{subtitulo}</p>
      </div>
      {acciones === undefined ? null : (
        <div className="cabecera__acciones">{acciones}</div>
      )}
    </header>
  );
}

/**
 * Lo que se abre encima para crear algo.
 *
 * Encima y no en otra pantalla porque quien da de alta un insumo está mirando la
 * lista de insumos: mandarlo a otro sitio le hace perder de vista lo que estaba
 * comparando, y volver le cuesta encontrar dónde estaba.
 */
/**
 * Un formulario encima de lo que había.
 *
 * Tenía el `Escape` colgado del `div` del velo, que no puede recibir el foco:
 * no se cerraba nunca con el teclado. Y al abrirse, el foco se quedaba en el
 * botón de detrás, así que tabular paseaba por la pantalla tapada mientras el
 * formulario estaba delante. Lo que hace ahora:
 *
 * - **Escape** escucha en el documento, no en un nodo que nadie enfoca.
 * - **El foco entra** al abrirse y **vuelve** al botón que lo abrió al cerrarse.
 *   Sin lo segundo, cerrar deja el foco en `body` y el siguiente tabulador
 *   empieza otra vez desde arriba de todo.
 * - **Tabular no se escapa**: el último campo lleva al primero.
 * - **Se cierra tocando fuera**, que es lo que todo el mundo intenta primero.
 * - **La página de detrás no rueda** mientras esto está abierto.
 */
export function Modal({
  titulo,
  children,
  onCerrar,
}: {
  titulo: string;
  children: ReactNode;
  onCerrar: () => void;
}) {
  const tituloId = useId();
  const caja = useRef<HTMLDivElement>(null);

  /*
   * Quien llama pasa una función nueva en cada render, así que si el efecto
   * dependiera de ella se reharía con cada tecla: el foco volvería al primer
   * campo mientras alguien escribe en el tercero. Se guarda en una referencia
   * y el efecto corre una sola vez, al abrir.
   */
  const cerrar = useRef(onCerrar);
  cerrar.current = onCerrar;

  useEffect(() => {
    const veniaDe = document.activeElement as HTMLElement | null;
    const desbordeAnterior = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const enfocables = () =>
      [
        ...(caja.current?.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        ) ?? []),
      ].filter((nodo) => !nodo.hasAttribute('disabled'));

    // El primer campo, no el primer botón: quien abre «Nueva receta» viene a
    // escribir, no a cancelar.
    const primeros = enfocables();
    (primeros.find((nodo) => nodo.tagName !== 'BUTTON') ?? primeros[0])?.focus();

    const alPulsar = (evento: globalThis.KeyboardEvent) => {
      if (evento.key === 'Escape') {
        evento.preventDefault();
        cerrar.current();
        return;
      }

      if (evento.key !== 'Tab') return;

      const lista = enfocables();
      const primero = lista[0];
      const ultimo = lista[lista.length - 1];
      if (primero === undefined || ultimo === undefined) return;

      if (evento.shiftKey && document.activeElement === primero) {
        evento.preventDefault();
        ultimo.focus();
      } else if (!evento.shiftKey && document.activeElement === ultimo) {
        evento.preventDefault();
        primero.focus();
      }
    };

    document.addEventListener('keydown', alPulsar);

    return () => {
      document.removeEventListener('keydown', alPulsar);
      document.body.style.overflow = desbordeAnterior;
      veniaDe?.focus();
    };
  }, []);

  return (
    <div
      className="velo"
      // Tocar fuera cierra; tocar dentro no. Sin lo segundo, arrastrar para
      // seleccionar texto dentro del formulario lo cerraba al soltar.
      onMouseDown={(evento) => {
        if (evento.target === evento.currentTarget) onCerrar();
      }}
    >
      <div
        ref={caja}
        role="dialog"
        aria-modal="true"
        aria-labelledby={tituloId}
      >
        <Tarjeta className="modal">
          <div className="modal__cabeza">
            <h2 id={tituloId} className="tarjeta__titulo">
              {titulo}
            </h2>
            <button
              type="button"
              className="modal__cerrar"
              aria-label="Cerrar"
              onClick={onCerrar}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                aria-hidden="true"
              >
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
          {children}
        </Tarjeta>
      </div>
    </div>
  );
}

/**
 * Una barra de progreso de stock: cuánto hay respecto del máximo.
 *
 * Con `aria-hidden` porque la cifra y la pastilla ya están al lado en texto: un
 * lector de pantalla que además leyera «68 por ciento» diría tres veces lo
 * mismo.
 */
export function BarraDeNivel({
  cantidad,
  maximo,
  tono,
}: {
  cantidad: string;
  maximo: string;
  tono: Tono;
}) {
  const actual = Number.parseFloat(cantidad);
  const techo = Number.parseFloat(maximo);
  const porcentaje =
    Number.isNaN(actual) || Number.isNaN(techo) || techo <= 0
      ? 0
      : Math.min(100, Math.max(0, (actual / techo) * 100));

  return (
    <div className="barra" aria-hidden="true">
      <div
        className={`barra__relleno barra__relleno--${tono}`}
        style={{ width: `${porcentaje}%` }}
      />
    </div>
  );
}
